import { ColumnHelper } from '@tanstack/react-table';
import { useMemo } from 'react';
import { ICreator } from '~/types';

import { SkeletonCell } from '../components/Table/SkeletonComponents';

interface useTableSkeletonProps<TData> {
  columnDefiners: {
    [key: string]: Parameters<ColumnHelper<TData>['accessor']>[1];
  };
  data: ICreator[];
  isLoading: boolean;
  numberOfRows: number;
  skeletonMapper: { [key: string]: JSX.Element };
  defaultSkeleton?: JSX.Element;
}
type UseTableSkeletonResult = {
  tableColumnDefiners: {
    [key: string]: Parameters<ColumnHelper<any>['accessor']>[1];
  };
  tableData: ICreator[];
};

export const useTableSkeleton = <TData,>({
  columnDefiners,
  data,
  isLoading,
  numberOfRows,
  skeletonMapper,
  defaultSkeleton = <SkeletonCell />,
}: useTableSkeletonProps<TData>): UseTableSkeletonResult => {
  const modifiedColumnDef = useMemo(() => {
    if (!isLoading) return columnDefiners;

    return Object.entries(columnDefiners).reduce((acc, [key, val]) => {
      if (isLoading) {
        return {
          ...acc,
          [key]: {
            ...val,
            cell: () => skeletonMapper[key] || defaultSkeleton,
          },
        };
      } else {
        return acc;
      }
    }, {});
  }, [columnDefiners, defaultSkeleton, isLoading, skeletonMapper]);

  const modifiedData = useMemo(() => {
    if (!isLoading) return data;
    return Array(numberOfRows).fill({});
  }, [data, isLoading, numberOfRows]);

  return { tableColumnDefiners: modifiedColumnDef, tableData: modifiedData };
};
